/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import {
  BuilderPermissionSet,
  BulkActionEntityType,
  BulkUsageResponse,
  UsageRestService,
  Usages,
} from '@gh/builder-shared-data';
import { getRouteInitData, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';

import {
  EstimatingTakeOffParamDetailsDialogConfig,
  EstimatingTakeOffParamsTabBusinessService,
} from '../../resources/estimating-take-off-params-tab-business.service';
import { TakeOffParam } from '../../resources/estimating-take-off-params-tab.model';
import { estimatingTakeOffParamDetailsDialog } from '../estimating-take-off-params-details-dialog/estimating-take-off-param-details-dialog.route';
import { EstimatingTakeOffParamsTabGridDefinition } from './estimating-take-off-params-tab-grid-definition.service';
import { newGridPaginationCursor } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-estimating-take-off-params-tab',
  templateUrl: 'estimating-take-off-params-tab.component.html',
  styleUrls: ['estimating-take-off-params-tab.component.scss'],
  viewProviders: [EstimatingTakeOffParamsTabGridDefinition],
})
export class EstimatingTakeOffParamsTabComponent implements OnInit {
  cursor = newGridPaginationCursor();
  showDeleted: boolean = false;
  dataSource: SlicedDataSource<TakeOffParam>;
  loadedData: any[] = [];
  activeLoadedData: any[] = [];
  checkList: any[] = [];
  get isShowDeleteSelected(): boolean {
    return !!this.checkList.length;
  }

  constructor(public gridDefinition: EstimatingTakeOffParamsTabGridDefinition,
              private userContext: UserContext,
              private businessService: EstimatingTakeOffParamsTabBusinessService,
              private pipelines: ListActionPipelines,
              private dialogService: DialogService,
              private commonActions: CommonActions,
              private navigationService: BuilderNavigationService,
              private notifications: NotificationService,
              private waitingService: WaitingService,
              private usageService: UsageRestService,
              private route: ActivatedRoute) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);
    this.loadData();
  }

  loadData(): void {
    const filter = {
      showDeleted: this.showDeleted,
    };
    this.dataSource = this.businessService.findAllAsDataSource(filter);
  }

  onCreate(): void {
    this.dialogService.openWithInput(estimatingTakeOffParamDetailsDialog, {}).afterClosed()
      .subscribe((takeOffParam) => {
        if (takeOffParam) {
          this.loadData();
        }
      });
  }

  onEdit({ takeOffParamId }: TakeOffParam): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.Estimating.TakeOffParameters.EDIT_VIEW)) {
      const input = { id: takeOffParamId };
      this.dialogService
        .openWithInput(estimatingTakeOffParamDetailsDialog, <EstimatingTakeOffParamDetailsDialogConfig>input)
        .afterClosed()
        .subscribe((takeOffParam) => {
          if (takeOffParam) {
            this.loadData();
          }
        });
    }
  }

  onDeactivate(takeOffParam: TakeOffParam): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_top_mark_as_deleted',
          params: {
            code: takeOffParam.code,
          },
        },
        confirmNotUsed: {
          id: 'msg_top_delete',
          params: {
            code: takeOffParam.code,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(takeOffParam, state.value)),
      },
      finish: () => this.loadData(),
    });

    flow.run({
      value: takeOffParam,
    });
  }

  onActivate(takeOffParam: TakeOffParam): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_top_undelete',
        params: {
          code: takeOffParam.code,
        },
      },
      execute: this.commonActions.execute((state: CommonFlowState<TakeOffParam>) =>
        this.businessService.activate(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: takeOffParam,
    });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId || void 0,
      entityType: entityType ? entityType : 'TOP',
      label: label ? label : 'lbl_top_full',
    });
  }

  onDeleteSelected(): void {
    const entityList = this.loadedData
      .filter((model) => this.checkList.includes(model.id))
      ?.map((item) => ({id: item.id, version: item.version}));

    this.usageService.getBulkUsageInfo(<any[]>entityList, BulkActionEntityType.takeOffParams)
      .pipe(this.waitingService.operator())
      .subscribe((res: BulkUsageResponse) => {
      this.dialogService.confirmation(
        yesNoConfirmation(res.allDeleted ? 'msg_top_delete_selected' : 'msg_top_delete_mark_as_deleted'),
      ).pipe(
        filter$(yesAnswer),
      ).subscribe(
        () => this.businessService.bulkDelete(res.usages.map((resItem: Usages) => ({
          id: resItem.itemId,
          usage: {
            version: resItem.version,
            deleted: resItem.deleted,
          },
        }))).subscribe(() => this.loadData(), this.notifications.processAll),
        this.notifications.processAll);
    },           this.notifications.processAll);
  }

  isActionButtonsVisible(): boolean {
    return !this.checkList.length;
  }

  onLoadData(event: any): void {
    this.checkList = [];
    this.loadedData = event.data.map((item: TakeOffParam) => ({...item, id: item.takeOffParamId}));
    this.activeLoadedData = this.loadedData.filter(this.canBeSelected);
  }

  rowClicked(event: any): void {
    if (event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.event.shiftKey) {
      this.onCheckChange(event.data.takeOffParamId);
      return;
    }
    if (this.isActionButtonsVisible()) {
      this.onEdit(event.data);
    }
  }

  onToggleSelectAll(): void {
    if (!this.checkList.length || (!!this.checkList.length && this.checkList.length < this.activeLoadedData.length)) {
      this.checkList = this.activeLoadedData?.map((item) => item.id);
      return;
    }
    this.checkList = [];
  }

  onCheckChange(id: ID): void {
    const targetItem = this.loadedData.find((item) => item.id === id);
    if (!targetItem || !this.canBeSelected(targetItem)) {
      return;
    }
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      return;
    }
    this.checkList = [...this.checkList, id];
  }

  canBeSelected(item: TakeOffParam): boolean {
    return item.isActive;
  }
}
